import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Navigation from '../../../Layouts/Navigation/Navigation';
import { BASE_URL } from '../../../utils/constants';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUsers, faCheckCircle, faClock, faTimesCircle, faMagnifyingGlass, faXmark } from '@fortawesome/free-solid-svg-icons';
import { format } from 'date-fns';

const VisitReport = () => {
  const [visits, setVisits] = useState([]);
  const [filteredVisits, setFilteredVisits] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedVisit, setSelectedVisit] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [stats, setStats] = useState({
    total: 0,
    completadas: 0,
    pendientes: 0,
    expiradas: 0
  });

  useEffect(() => {
    const fetchVisits = async () => {
      try {
        const response = await axios.get(`${BASE_URL}/allVisits`);
        const allVisits = response.data;
        setVisits(allVisits);
        setFilteredVisits(allVisits);
        
        // Calcular estadísticas
        const total = allVisits.length;
        const completadas = allVisits.filter(v => v.flag_ingreso === 1).length;
        const pendientes = allVisits.filter(v => v.flag_ingreso === 0).length;
        const expiradas = allVisits.filter(v => v.flag_ingreso === 2).length;
        
        setStats({ total, completadas, pendientes, expiradas });
      } catch (error) {
        console.error('Error fetching visits:', error);
      }
    };

    fetchVisits();
  }, []);

  const handleSearch = (event) => {
    const value = event.target.value.toLowerCase();
    setSearchTerm(value);

    const filtered = visits.filter((visit) =>
      visit.nombre.toLowerCase().includes(value) ||
      visit.apellido.toLowerCase().includes(value) ||
      visit.torre_nombre.toLowerCase().includes(value) ||
      visit.apto_num_apartamento.toString().includes(value) ||
      visit.placa?.toLowerCase().includes(value)
    );
    setFilteredVisits(filtered);
    setPage(0);
  };

  const formatDate = (dateString) => {
    return format(new Date(dateString), 'dd/MM/yyyy hh:mm a');
  };

  const getStatusColor = (flag) => {
    switch (flag) {
      case 1: return 'bg-green-100 text-green-800';
      case 0: return 'bg-yellow-100 text-yellow-800';
      case 2: return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (flag) => {
    switch (flag) {
      case 1: return 'Ingresado';
      case 0: return 'Pendiente';
      case 2: return 'Expirado';
      default: return 'Desconocido';
    }
  };

  const handleOpenDialog = (visit) => {
    setSelectedVisit(visit);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setSelectedVisit(null);
    setOpenDialog(false);
  };

  return (
    <>
      <Navigation />
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 sm:p-6 lg:p-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-center mb-8">
            <FontAwesomeIcon icon={faUsers} className="h-10 w-10 text-primary-600 mr-3" />
            <h1 className="text-4xl font-bold text-gray-800">
              Reporte General de Visitas
            </h1>
          </div>

          {/* Estadísticas */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-xl shadow-md p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Total Visitas</p>
                  <p className="text-3xl font-bold text-gray-800">{stats.total}</p>
                </div>
                <FontAwesomeIcon icon={faUsers} className="h-12 w-12 text-blue-500" />
              </div>
            </div>
            
            <div className="bg-white rounded-xl shadow-md p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Completadas</p>
                  <p className="text-3xl font-bold text-green-600">{stats.completadas}</p>
                </div>
                <FontAwesomeIcon icon={faCheckCircle} className="h-12 w-12 text-green-500" />
              </div>
            </div>
            
            <div className="bg-white rounded-xl shadow-md p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Pendientes</p>
                  <p className="text-3xl font-bold text-yellow-600">{stats.pendientes}</p>
                </div>
                <FontAwesomeIcon icon={faClock} className="h-12 w-12 text-yellow-500" />
              </div>
            </div>
            
            <div className="bg-white rounded-xl shadow-md p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Expiradas</p>
                  <p className="text-3xl font-bold text-red-600">{stats.expiradas}</p>
                </div>
                <FontAwesomeIcon icon={faTimesCircle} className="h-12 w-12 text-red-500" />
              </div>
            </div>
          </div>

          {/* Buscador */}
          <div className="bg-white rounded-xl shadow-md p-4 mb-6">
            <div className="relative">
              <FontAwesomeIcon icon={faMagnifyingGlass} className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Buscar por nombre, apellido, torre, apartamento o placa..."
                value={searchTerm}
                onChange={handleSearch}
              />
            </div>
          </div>

          {/* Tabla */}
          <div className="bg-white rounded-xl shadow-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gradient-to-r from-primary-500 to-primary-700">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-bold text-white uppercase tracking-wider">Fecha</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-white uppercase tracking-wider">Torre</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-white uppercase tracking-wider">Apto</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-white uppercase tracking-wider">Visitante</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-white uppercase tracking-wider">Acomp.</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-white uppercase tracking-wider">Vehículo</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-white uppercase tracking-wider">Placa</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-white uppercase tracking-wider">Estado</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-white uppercase tracking-wider">Acción</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredVisits
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((visit) => (
                      <tr key={visit.id_visita} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3 text-sm text-gray-700">{formatDate(visit.fecha)}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">{visit.torre_nombre}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">{visit.apto_num_apartamento}</td>
                        <td className="px-4 py-3 text-sm text-gray-900 font-medium">{visit.nombre} {visit.apellido}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">{visit.num_acompanantes}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">{visit.tipo_vehiculo || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">{visit.placa || '-'}</td>
                        <td className="px-4 py-3 text-sm">
                          <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(visit.flag_ingreso)}`}>
                            {getStatusText(visit.flag_ingreso)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <button
                            onClick={() => handleOpenDialog(visit)}
                            className="text-primary-600 hover:text-primary-800 font-medium"
                          >
                            Ver detalles
                          </button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>

            {/* Paginación */}
            <div className="bg-gray-50 px-6 py-4 flex items-center justify-between border-t border-gray-200">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-700">Filas por página:</span>
                <select
                  value={rowsPerPage}
                  onChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value));
                    setPage(0);
                  }}
                  className="border border-gray-300 rounded px-2 py-1 text-sm"
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
              </div>
              <div className="flex items-center gap-4">
                <span className="text-sm text-gray-700">
                  {page * rowsPerPage + 1}-{Math.min((page + 1) * rowsPerPage, filteredVisits.length)} de {filteredVisits.length}
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage(page - 1)}
                    disabled={page === 0}
                    className="px-3 py-1 bg-white border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                  >
                    Anterior
                  </button>
                  <button
                    onClick={() => setPage(page + 1)}
                    disabled={page >= Math.ceil(filteredVisits.length / rowsPerPage) - 1}
                    className="px-3 py-1 bg-white border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                  >
                    Siguiente
                  </button>
                </div>
              </div>
            </div>
          </div>

          {filteredVisits.length === 0 && (
            <div className="text-center py-12 bg-white rounded-xl shadow-md mt-6">
              <FontAwesomeIcon icon={faUsers} className="h-24 w-24 text-gray-300 mx-auto mb-4" />
              <p className="text-xl text-gray-500">No se encontraron visitas</p>
            </div>
          )}
        </div>
      </div>

      {/* Modal de detalles */}
      {openDialog && selectedVisit && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-2xl flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-800">Detalles de la Visita</h2>
              <button onClick={handleCloseDialog} className="text-gray-500 hover:text-gray-700">
                <FontAwesomeIcon icon={faXmark} className="h-6 w-6" />
              </button>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Estado</p>
                  <span className={`inline-block px-3 py-1 rounded-full text-sm font-semibold mt-1 ${getStatusColor(selectedVisit.flag_ingreso)}`}>
                    {getStatusText(selectedVisit.flag_ingreso)}
                  </span>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Fecha</p>
                  <p className="text-sm font-medium text-gray-900 mt-1">{formatDate(selectedVisit.fecha)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Visitante</p>
                  <p className="text-sm font-medium text-gray-900 mt-1">{selectedVisit.nombre} {selectedVisit.apellido}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Torre</p>
                  <p className="text-sm font-medium text-gray-900 mt-1">{selectedVisit.torre_nombre}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Apartamento</p>
                  <p className="text-sm font-medium text-gray-900 mt-1">{selectedVisit.apto_num_apartamento}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Acompañantes</p>
                  <p className="text-sm font-medium text-gray-900 mt-1">{selectedVisit.num_acompanantes}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Tipo de Vehículo</p>
                  <p className="text-sm font-medium text-gray-900 mt-1">{selectedVisit.tipo_vehiculo || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Placa</p>
                  <p className="text-sm font-medium text-gray-900 mt-1">{selectedVisit.placa || 'N/A'}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-sm text-gray-600">Observaciones</p>
                  <p className="text-sm font-medium text-gray-900 mt-1">{selectedVisit.observacion || 'Sin observaciones'}</p>
                </div>
              </div>
            </div>
            <div className="sticky bottom-0 bg-gray-50 px-6 py-4 rounded-b-2xl border-t border-gray-200">
              <button
                onClick={handleCloseDialog}
                className="w-full btn-primary"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default VisitReport;
